import { inject, Injectable } from '@angular/core';
import { UserSalesOrganisationService } from '@softapps/core';
import { MASTERDATA_CUSTOMER, MasterdataService } from '@softapps/core/masterdata';
import {
  ApiDataAccess,
  getValue,
  IsoDate,
  Query,
  QueryBuilder,
  QueryFilter,
} from '@softline/core';
import { firstValueFrom } from 'rxjs';
import { DocumentGridDataSource } from '../grid/query/document-grid.data-source';
import { getDocumentQueryTypePrefix } from '../helper/document-query/document-query-prefix.function';
import { DocumentQueryRow } from '../types/document-query';
import { FilterOptionEntry } from '../types/document-filter-option';
import { AbortedRequestError } from '@softline/core';

/** Document fields with no `document_date` to scope the option query by. */
const NO_DATE_TYPES: ReadonlySet<string> = new Set([
  'WORK_ORDER',
  'CONSOLIDATED_MEASUREMENT_REPORT',
  'MATERIAL_DELIVERY_NOTE',
]);

/** Bounded window for the row-scan fallback (used only when the document
 *  endpoint can't aggregate). Counts/values beyond this are not surfaced. */
const ROW_SCAN_MAX_ROWS = 1000;

export interface DocumentFilterLoadResult {
  items: FilterOptionEntry[];
  totalRows: number | null;
  /** True when the list/count is window-bounded or a masterdata list not
   *  narrowed to the current document set — not an exact aggregation. */
  approximate: boolean;
}

export interface DocumentFilterLoadParams {
  documentType: string;
  /** Already prefix-resolved query field, e.g. `project.project`. */
  field: string;
  labelField?: string;
  subLabelField?: string;
  uniqueField?: string;
  /** Page window (group rows). */
  start: number;
  end: number;
  search?: string;
  /** Date scope from the page (skipped for NO_DATE_TYPES). */
  dateFrom: IsoDate;
  dateTo: IsoDate;
  /** Active filters from the OTHER dimensions (cross-filtering). */
  crossFilters: QueryFilter<DocumentQueryRow>[];
}

/**
 * Hybrid loader for the document-query filter sidebar option lists. Routes per
 * dimension: customers → masterdata search (exact); sales organisations →
 * user-context resource (static list); everything else → a grouped query against the
 * document endpoint (exact), falling back to a bounded row-scan + client dedup
 * (approximate) when the endpoint doesn't support grouped/aggregate columns.
 *
 * Route-scoped; injects `ApiDataAccess` which resolves to
 * `AbortableApiDataAccess` at the document-query route so loads can abort.
 */
@Injectable()
export class SalesDocumentFilterService {
  private readonly dataAccess = inject(ApiDataAccess);
  private readonly masterdata = inject(MasterdataService);
  private readonly salesOrganisations = inject(UserSalesOrganisationService);

  /** Per-documentType verdict on whether grouped/aggregate columns work.
   *  `undefined` = not probed yet; once a grouped query errors we cache `false`
   *  so later dimensions skip the failing grouped attempt. */
  private readonly supportsGrouping = new Map<string, boolean>();

  async loadFilter(
    params: DocumentFilterLoadParams,
    signal?: AbortSignal
  ): Promise<DocumentFilterLoadResult> {
    const dimension = params.field;
    if (dimension === 'customer.number') {
      return this.loadCustomers(params);
    }
    if (dimension === 'sales_organisation.sales_organisation') {
      return this.loadSalesOrganisations(params);
    }
    return this.loadFromDocuments(params, signal);
  }

  // --- Dedicated routes ------------------------------------------------------

  /** Customers via the masterdata search endpoint (server search + paging,
   *  exact). Value is `"<number>,<subNumber>"` to match the customer filter. */
  private async loadCustomers(
    params: DocumentFilterLoadParams
  ): Promise<DocumentFilterLoadResult> {
    const limit = params.end - params.start;
    const { items: customers, totalCount } = await firstValueFrom(
      this.masterdata.searchWithCount$(MASTERDATA_CUSTOMER, params.search ?? null, {
        limit,
        offset: params.start,
      })
    );
    const items: FilterOptionEntry[] = customers.map((c) => {
      const number = c.customerNumber;
      const sub = c.customerSubnumber ?? 0;
      const value = `${number},${sub}`;
      const name = [c.name1, c.name2, c.name3].filter(Boolean).join(' ').trim();
      const numberLabel = sub > 0 ? `${number}/${sub}` : `${number}`;
      return {
        value,
        label: name ? `${numberLabel} - ${name}` : numberLabel,
        subLabel: name || undefined,
      };
    });
    // Exact values, but the list isn't narrowed to the current document set.
    return { items, totalRows: totalCount, approximate: true };
  }

  /** Sales organisations from the user-context resource (static, client-side). */
  private loadSalesOrganisations(
    params: DocumentFilterLoadParams
  ): DocumentFilterLoadResult {
    const all = this.salesOrganisations.value();
    const search = (params.search ?? '').toLowerCase().trim();
    const items: FilterOptionEntry[] = all
      .map((o) => {
        const value = (o.vkforg ?? '').trim();
        return { value, label: value };
      })
      .filter((o) => o.value !== '')
      .filter((o) => (search ? o.label.toLowerCase().includes(search) : true));
    const page = items.slice(params.start, params.end);
    return { items: page, totalRows: items.length, approximate: false };
  }

  // --- Document-derived routes ----------------------------------------------

  /** Grouped query (exact) with a row-scan fallback (approximate) when the
   *  document endpoint rejects grouped/aggregate columns. */
  private async loadFromDocuments(
    params: DocumentFilterLoadParams,
    signal?: AbortSignal
  ): Promise<DocumentFilterLoadResult> {
    const ds = new DocumentGridDataSource(this.dataAccess, params.documentType);
    const grouped = this.supportsGrouping.get(params.documentType);
    if (grouped !== false) {
      try {
        const result = await this.runGrouped(ds, params, signal);
        this.supportsGrouping.set(params.documentType, true);
        return result;
      } catch (e) {
        if (e instanceof AbortedRequestError) throw e;
        // Grouping unsupported for this documentType — remember and fall back.
        this.supportsGrouping.set(params.documentType, false);
      }
    }
    return this.runRowScan(ds, params, signal);
  }

  private async runGrouped(
    ds: DocumentGridDataSource,
    params: DocumentFilterLoadParams,
    signal?: AbortSignal
  ): Promise<DocumentFilterLoadResult> {
    const { field, labelField, subLabelField } = params;
    const builder = new QueryBuilder<DocumentQueryRow>()
      .start(params.start)
      .end(params.end)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .column(field as any, { usage: 'group' })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .column(field as any, { usage: 'aggregate', agg: 'totalcount' as any })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .sort(field as any, 'ASC');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (labelField) builder.column(labelField as any, { usage: 'group' });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (subLabelField) builder.column(subLabelField as any, { usage: 'group' });
    this.applyScopeFilters(builder, params);

    const result = await ds.loadAbortable(
      { query: builder.build() as Query<DocumentQueryRow> },
      signal
    );
    const rows = (result?.data ?? []) as DocumentQueryRow[];
    const items = rows.map((row) => this.toEntry(row, params));
    const [prefix, leaf] = field.split('.');
    const firstRow = rows[0] as unknown as
      Record<string, Record<string, unknown>> | undefined;
    const totalRows =
      rows.length === 0
        ? 0
        : ((firstRow?.[prefix]?.[`${leaf}:totalcount`] as number) ?? null);
    return { items, totalRows, approximate: false };
  }

  /** Bounded row scan + client-side dedup. Always works, but values/counts are
   *  limited to the fetched window. */
  private async runRowScan(
    ds: DocumentGridDataSource,
    params: DocumentFilterLoadParams,
    signal?: AbortSignal
  ): Promise<DocumentFilterLoadResult> {
    const { field, labelField, subLabelField } = params;
    const builder = new QueryBuilder<DocumentQueryRow>().start(0).end(ROW_SCAN_MAX_ROWS);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    builder.column(field as any, { usage: 'group' });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (labelField) builder.column(labelField as any, { usage: 'group' });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (subLabelField) builder.column(subLabelField as any, { usage: 'group' });
    this.applyScopeFilters(builder, params);

    const result = await ds.loadAbortable(
      { query: builder.build() as Query<DocumentQueryRow> },
      signal
    );
    const rows = (result?.data ?? []) as DocumentQueryRow[];
    const byValue = new Map<string, FilterOptionEntry>();
    for (const row of rows) {
      const entry = this.toEntry(row, params);
      if (entry.value && !byValue.has(entry.value)) byValue.set(entry.value, entry);
    }
    const all = [...byValue.values()].sort((a, b) => a.value.localeCompare(b.value));
    const page = all.slice(params.start, params.end);
    return { items: page, totalRows: all.length, approximate: true };
  }

  // --- Shared helpers --------------------------------------------------------

  /** Date scope (per type) + search tokens + cross-filters. */
  private applyScopeFilters(
    builder: QueryBuilder<DocumentQueryRow>,
    params: DocumentFilterLoadParams
  ): void {
    if (!NO_DATE_TYPES.has(params.documentType)) {
      const prefix = getDocumentQueryTypePrefix(params.documentType);
      builder.filter({
        type: 'dateInRange',
        name: `${prefix}.document_date`,
        valueFrom: params.dateFrom,
        valueTo: params.dateTo,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }

    const search = params.search?.trim();
    if (search) {
      const columns = [params.field, params.labelField, params.subLabelField].filter(
        (c): c is string => !!c
      );
      const tokens = search.split(/\s+/).filter(Boolean);
      const tokenFilters = tokens.map((token) => {
        const fieldFilters = columns.map(
          (column) =>
            ({
              type: 'stringContainsIgnoreCase',
              name: column,
              value: token,
            }) as QueryFilter<DocumentQueryRow>
        );
        return fieldFilters.length === 1
          ? fieldFilters[0]
          : ({
              type: 'conditions',
              operator: 'OR',
              filters: fieldFilters,
            } as unknown as QueryFilter<DocumentQueryRow>);
      });
      if (tokenFilters.length === 1) builder.filter(tokenFilters[0]);
      else if (tokenFilters.length > 1)
        builder.filter({
          type: 'conditions',
          operator: 'AND',
          filters: tokenFilters,
        } as unknown as QueryFilter<DocumentQueryRow>);
    }

    for (const f of params.crossFilters) builder.filter(f);
  }

  /** Maps a (grouped or row) result row to a `{value,label,subLabel}` entry. */
  private toEntry(
    row: DocumentQueryRow,
    params: DocumentFilterLoadParams
  ): FilterOptionEntry {
    const value = String(getValue(row, params.field) ?? '');
    const labelText = params.labelField
      ? String(getValue(row, params.labelField) ?? '')
      : '';
    const subLabel = params.subLabelField
      ? (getValue(row, params.subLabelField) as string) || undefined
      : undefined;
    const label = labelText && labelText !== value ? `${value} - ${labelText}` : value;
    return subLabel ? { value, label, subLabel } : { value, label };
  }
}
